import React, { useEffect, useRef } from 'react';

interface CanvasProps {
  content: string;
  onClose: () => void;
}

const Canvas: React.FC<CanvasProps> = ({ content, onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = 800;
    canvas.height = 600;

    // Clear canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Create a sample visualization based on content
    if (content.toLowerCase().includes('chart') || content.toLowerCase().includes('data')) {
      drawChart(ctx, canvas.width, canvas.height);
    } else if (content.toLowerCase().includes('diagram')) {
      drawDiagram(ctx, canvas.width, canvas.height);
    } else {
      drawDefault(ctx, canvas.width, canvas.height);
    }
  }, [content]);

  const drawChart = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    // Draw chart background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(50, 50, width - 100, height - 100);
    
    // Draw axes
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(80, height - 80);
    ctx.lineTo(width - 50, height - 80);
    ctx.moveTo(80, height - 80);
    ctx.lineTo(80, 80);
    ctx.stroke();

    // Draw data points and lines
    const dataPoints = [
      { x: 120, y: height - 150 },
      { x: 200, y: height - 200 },
      { x: 280, y: height - 180 },
      { x: 360, y: height - 250 },
      { x: 440, y: height - 220 },
      { x: 520, y: height - 280 },
      { x: 600, y: height - 300 },
      { x: 680, y: height - 260 }
    ];

    // Draw line
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(dataPoints[0].x, dataPoints[0].y);
    dataPoints.forEach(point => ctx.lineTo(point.x, point.y));
    ctx.stroke();

    // Draw points
    ctx.fillStyle = '#3b82f6';
    dataPoints.forEach(point => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, 6, 0, Math.PI * 2);
      ctx.fill();
    });

    // Add labels
    ctx.fillStyle = '#374151';
    ctx.font = '14px Inter, sans-serif';
    ctx.fillText('Time', width / 2 - 20, height - 20);
    ctx.save();
    ctx.translate(20, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Value', -20, 0);
    ctx.restore();

    // Add title
    ctx.font = 'bold 18px Inter, sans-serif';
    ctx.fillText('Interactive Data Visualization', width / 2 - 120, 30);
  };

  const drawDiagram = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    // Draw diagram nodes
    const nodes = [
      { x: 150, y: 150, label: 'Input' },
      { x: 400, y: 100, label: 'Process A' },
      { x: 400, y: 200, label: 'Process B' },
      { x: 650, y: 150, label: 'Output' }
    ];

    // Draw connections
    ctx.strokeStyle = '#6b7280';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(190, 150);
    ctx.lineTo(360, 100);
    ctx.moveTo(190, 150);
    ctx.lineTo(360, 200);
    ctx.moveTo(440, 100);
    ctx.lineTo(610, 150);
    ctx.moveTo(440, 200);
    ctx.lineTo(610, 150);
    ctx.stroke();

    // Draw arrows
    const drawArrow = (fromX: number, fromY: number, toX: number, toY: number) => {
      const angle = Math.atan2(toY - fromY, toX - fromX);
      const arrowLength = 10;
      
      ctx.beginPath();
      ctx.moveTo(toX, toY);
      ctx.lineTo(
        toX - arrowLength * Math.cos(angle - Math.PI / 6),
        toY - arrowLength * Math.sin(angle - Math.PI / 6)
      );
      ctx.moveTo(toX, toY);
      ctx.lineTo(
        toX - arrowLength * Math.cos(angle + Math.PI / 6),
        toY - arrowLength * Math.sin(angle + Math.PI / 6)
      );
      ctx.stroke();
    };

    drawArrow(190, 150, 360, 100);
    drawArrow(190, 150, 360, 200);
    drawArrow(440, 100, 610, 150);
    drawArrow(440, 200, 610, 150);

    // Draw nodes
    nodes.forEach(node => {
      ctx.fillStyle = '#3b82f6';
      ctx.beginPath();
      ctx.roundRect(node.x - 40, node.y - 20, 80, 40, 8);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.font = '14px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(node.label, node.x, node.y + 5);
    });

    // Add title
    ctx.fillStyle = '#374151';
    ctx.font = 'bold 18px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('System Architecture Diagram', width / 2, 30);
  };

  const drawDefault = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    // Draw a creative visualization
    const centerX = width / 2;
    const centerY = height / 2;

    // Draw concentric circles with gradient
    for (let i = 5; i > 0; i--) {
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, i * 40);
      gradient.addColorStop(0, `rgba(59, 130, 246, ${0.1 * i})`);
      gradient.addColorStop(1, `rgba(59, 130, 246, 0)`);
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, i * 40, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw connecting lines
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI * 2) / 8;
      const x1 = centerX + Math.cos(angle) * 50;
      const y1 = centerY + Math.sin(angle) * 50;
      const x2 = centerX + Math.cos(angle) * 150;
      const y2 = centerY + Math.sin(angle) * 150;
      
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    // Add title
    ctx.fillStyle = '#374151';
    ctx.font = 'bold 18px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Interactive Canvas Visualization', centerX, 50);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Canvas View</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Canvas Content */}
        <div className="p-6">
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <canvas
              ref={canvasRef}
              className="w-full h-auto border border-gray-200 rounded"
              style={{ maxHeight: '400px' }}
            />
          </div>

          {/* Interactive Controls */}
          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200">
                Zoom In
              </button>
              <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200">
                Zoom Out
              </button>
              <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200">
                Reset View
              </button>
            </div>
            <div className="text-sm text-gray-500">
              Interactive visualization powered by Canvas API
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Canvas;
